import React, { useState, useRef, useEffect } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Login from '../../Login';
import SignUp from '../../SignUp';
import { API_BASE_URL } from '../../config';

const LandingPage = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpPrefill, setSignUpPrefill] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formStep, setFormStep] = useState(1); // 1: agent selection, 2: user details
  const [showContactSales, setShowContactSales] = useState(false);
  const [contactSalesData, setContactSalesData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isStartingDemo, setIsStartingDemo] = useState(false);
  const [isFormHidden, setIsFormHidden] = useState(false);
  
  // Call state variables
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [callError, setCallError] = useState('');
  const retellClient = useRef(null);

  // Agent configurations with IDs (keep in sync with server.js ALLOWED_DEMO_AGENT_IDS; one common from_number used for all)
  const agents = [
    {
      name: 'Hotel Receptionist',
      id: 'agent_259be7918585ee854e1ffe3edb',
      icon: '🏨'
    },
    {
      name: 'Customer Support',
      id: 'agent_1391e8a09211d496457e71ea0a',
      icon: '😊'
    },
    {
      name: 'Appointment Setter',
      id: 'agent_73fbe7873f817e3d96ce1da389',
      icon: '📅'
    },
    {
      name: 'Book Store',
      id: 'agent_56be987ba50b65cdac02aca96e',
      icon: '📚'
    },
    {
      name: 'Gym Business',
      id: 'agent_fc22fa6a2df487ee5499327e40',
      icon: '💪'
    },
    {
      name: 'Real Estate',
      id: 'agent_b209b1736719c5e59d4a48f843',
      icon: '🏠'
    }
  ];

  const handleLogin = () => {
    setShowLogin(true);
  };

  const handleSignUp = () => {
    setShowSignUp(true);
  };

  const handleCreateChatbot = () => {
    setShowSignUp(true);
  };

  const handleSwitchToLogin = () => {
    setShowSignUp(false);
    setShowLogin(true);
  };

  const handleSwitchToSignUp = () => {
    setShowLogin(false);
    setShowSignUp(true);
  };

  const handleTryForFree = () => {
    const demoSection = document.getElementById('demo-agents-section');
    if (demoSection) {
      demoSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleContactSales = () => {
    setShowContactSales(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpenSignUp = params.get('openSignup') === '1';
    const requestedMode = params.get('mode');

    if (!shouldOpenSignUp || requestedMode !== 'appointment') return;

    setSignUpPrefill({
      email: params.get('email') || '',
      name: params.get('name') || '',
      chatbotName: params.get('chatbotName') || '',
      mode: requestedMode || ''
    });
    setShowSignUp(true);

    const nextUrl = `${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, []);

  const handleContactSalesClose = () => {
    // End any active call before closing
    if (isCallActive) {
      endCall();
    }
    setShowContactSales(false);
    setContactSalesData({ name: '', email: '', phone: '' });
    // Reset call states
    setCallStatus('');
    setCallError('');
    setIsCallActive(false);
    setIsConnecting(false);
    setIsFormHidden(false);
  };

  const handleContactSalesInputChange = (e) => {
    const { name, value } = e.target;
    setContactSalesData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Setup call event listeners
  const setupCallEventListeners = () => {
    const client = retellClient.current;
    client.on('call_started', () => {
      setCallStatus('Call connected! You can now speak.');
      setIsCallActive(true);
      setIsConnecting(false);
    });
    client.on('call_ended', () => {
      setCallStatus('Call ended.');
      setIsCallActive(false);
      setIsConnecting(false);
    });
    client.on('agent_start_talking', () => {
      setCallStatus('Agent is speaking...');
    });
    client.on('agent_stop_talking', () => {
      setCallStatus('Listening... You can speak now.');
    });
    client.on('error', (error) => {
      setCallError(`Call error: ${error.message || error}`);
      setIsCallActive(false);
      setIsConnecting(false);
    });
    client.on('update', (update) => {
      // Optionally handle real-time updates
    });
  };

  // End call function
  const endCall = () => {
    if (retellClient.current) {
      try {
        retellClient.current.stopCall();
      } catch (error) {
        console.error('Error stopping call:', error);
      }
    }
    setCallStatus('Call ended.');
    setIsCallActive(false);
    setIsConnecting(false);
  };

  // Cleanup call on component unmount
  useEffect(() => {
    return () => {
      if (retellClient.current) {
        try {
          retellClient.current.stopCall();
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    };
  }, []);

  const handleStartDemo = async () => {
    if (!contactSalesData.name || !contactSalesData.email || !contactSalesData.phone) {
      alert('Please fill in all fields before starting the demo.');
      return;
    }

    setIsStartingDemo(true);
    setCallError('');
    setCallStatus('Creating call...');

    try {
      // Create web call for the sales agent first
      console.log('Creating web call demo with agent:', 'agent_f0f5275981f094b9bb67159a31');
      
      const webCallResponse = await fetch(`${API_BASE_URL}/api/ai-assistant/web-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: 'agent_f0f5275981f094b9bb67159a31',
          user_email: contactSalesData.email,
          retell_llm_dynamic_variables: {
            user_name: contactSalesData.name,
            user_email: contactSalesData.email,
            user_phone: contactSalesData.phone,
            demo_type: 'contact_sales'
          },
          metadata: {
            demo_type: 'contact_sales',
            user_name: contactSalesData.name,
            user_phone: contactSalesData.phone
          }
        })
      });

      if (webCallResponse.ok) {
        const webCallData = await webCallResponse.json();
        
        // Store contact sales data with the actual call_id from web call response
        const response = await fetch(`${API_BASE_URL}/api/contact-sales-demo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...contactSalesData,
            agentId: 'agent_f0f5275981f094b9bb67159a31',
            agentName: 'Sales Agent',
            callId: webCallData.call_id // Use the actual call_id from web call
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setCallStatus('Connecting...');
          
          // Initialize Retell client and start the call
          retellClient.current = new RetellWebClient();
          setupCallEventListeners();
          
          await retellClient.current.startCall({
            accessToken: webCallData.access_token,
            sampleRate: 24000,
            emitRawAudioSamples: false,
          });
          
          // Hide the form fields after successful call start
          setIsStartingDemo(false);
          setIsFormHidden(true);
        } else {
          setCallError(result.error || 'Failed to store demo data. Please try again.');
          setIsStartingDemo(false);
        }
      } else {
        const webCallError = await webCallResponse.json();
        setCallError(`Failed to create web call: ${webCallError.error || 'Unknown error'}`);
        setIsStartingDemo(false);
      }
    } catch (error) {
      console.error('Error starting demo:', error);
      setCallError('Network error. Please check your connection and try again.');
      setIsStartingDemo(false);
    }
  };

  const handleAgentSelection = (agent) => {
    setSelectedAgent(agent);
    setFormStep(2);
  };

  const handleBackToAgentSelection = () => {
    setSelectedAgent(null);
    setFormStep(1);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const userData = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      agentId: selectedAgent.id,
      agentName: selectedAgent.name
    };

    console.log('📝 Frontend sending user data:', userData);

    try {
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.textContent = 'Submitting...';
      submitButton.disabled = true;

             // Make API call to store demo agent details
       const response = await fetch(`${API_BASE_URL}/api/demo-agent-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Call ID from the call itself (Retell); Request ID when call was not created (request_reference)
        const idLabel = (result.data.call_id_source === 'request_reference' || result.warning) ? 'Request ID' : 'Call ID';
        let message = `✅ ${result.message}\n\nDemo Request Details:\n• Name: ${userData.name}\n• Email: ${userData.email}\n• Phone: ${userData.phone}\n• Agent: ${userData.agentName}\n• ${idLabel}: ${result.data.call_id}`;
        
        // Add call status information when we have a real Retell call
        if (result.data.retell_call_id) {
          message += `\n• Retell Call ID: ${result.data.retell_call_id}\n• Call Status: ${result.data.call_status}`;
        }
        
        if (result.warning) {
          message += `\n\n⚠️ ${result.warning}`;
        }
        
        alert(message);
        
        // Reset form
        setSelectedAgent(null);
        setFormStep(1);
        e.target.reset();
      } else {
        // Show error message
        alert(`❌ ${result.error || 'Failed to submit demo request. Please try again.'}`);
      }
    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert('❌ Network error. Please check your connection and try again.');
    } finally {
      // Reset button state
      const submitButton = e.target.querySelector('button[type="submit"]');
      submitButton.textContent = 'SUBMIT DEMO REQUEST';
      submitButton.disabled = false;
    }
  };



  // Handle FAQ accordion toggle
  const handleFAQToggle = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Mobile-first responsive styles
  const mobileStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px', // Reduced padding for mobile
      width: '100%',
      boxSizing: 'border-box'
    },
    heroSection: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '120px 0 60px 0', // Increased top padding to account for header height
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1 // Ensure content is above any potential overlapping elements
    },
    heroTitle: {
      fontSize: 'clamp(2rem, 5vw, 3.2rem)', // Responsive font size
      fontWeight: 800,
      marginBottom: 20,
      lineHeight: 1.2,
      padding: '0 16px',
      position: 'relative',
      zIndex: 2 // Ensure text is above any background elements
    },
    heroSubtitle: {
      fontSize: 'clamp(1rem, 3vw, 1.3rem)', // Responsive font size
      maxWidth: 600,
      margin: '0 auto 32px auto',
      opacity: 0.95,
      padding: '0 16px',
      lineHeight: 1.5,
      position: 'relative',
      zIndex: 2 // Ensure text is above any background elements
    },
    heroButtons: {
      display: 'flex',
      gap: 16,
      justifyContent: 'center',
      flexWrap: 'wrap',
      padding: '0 16px',
      position: 'relative',
      zIndex: 2 // Ensure buttons are above any background elements
    },
    heroButton: {
      background: 'white',
      color: '#667eea',
      border: 'none',
      padding: '12px 24px', // Reduced padding for mobile
      borderRadius: 8,
      fontWeight: 700,
      fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', // Responsive font size
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      minWidth: '120px'
    },
    heroButtonSecondary: {
      background: 'transparent',
      color: 'white',
      border: '2px solid white',
      padding: '12px 24px', // Reduced padding for mobile
      borderRadius: 8,
      fontWeight: 700,
      fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', // Responsive font size
      cursor: 'pointer',
      minWidth: '120px'
    },
    section: {
      background: 'white',
      padding: '60px 0', // Reduced padding for mobile
      width: '100%',
      boxSizing: 'border-box'
    },
    sectionTitle: {
      fontSize: 'clamp(1.8rem, 4vw, 2.2rem)', // Responsive font size
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: 40,
      padding: '0 16px'
    },
    sectorsGrid: {
      display: 'flex',
      flexDirection: 'row', // Horizontal layout
      flexWrap: 'wrap', // Wrap on mobile
      gap: '40px', // Increased gap between columns
      justifyContent: 'center',
      alignItems: 'flex-start', // Align items to top
      padding: '0 16px'
    },
    sectorsColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      flex: '1 1 400px', // Each column takes equal space
      maxWidth: '500px',
      minWidth: '300px'
    },
    videoColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '1 1 400px',
      maxWidth: '500px',
      minWidth: '300px'
    },
    videoContainer: {
      width: '100%',
      maxWidth: '600px', // Increased from 480px
      minHeight: '400px', // Increased from 320px
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      background: 'transparent'
    },
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    },
    sectorCard: {
      display: 'flex',
      alignItems: 'center',
      background: '#f3f4f6',
      borderRadius: 16,
      padding: '20px', // Reduced padding for mobile
      width: '100%', // Full width on mobile
      maxWidth: '480px',
      minWidth: 'auto', // Remove fixed min-width
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      boxSizing: 'border-box'
    },
    sectorImage: {
      width: '60px', // Smaller on mobile
      height: '60px', // Smaller on mobile
      borderRadius: 12,
      objectFit: 'cover',
      marginRight: '16px', // Reduced margin for mobile
      flexShrink: 0
    },
    sectorContent: {
      flex: 1,
      minWidth: 0 // Allow text to wrap
    },
    sectorTitle: {
      fontWeight: 700,
      fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', // Responsive font size
      marginBottom: 8,
      lineHeight: 1.3
    },
    sectorDescription: {
      color: '#64748b',
      marginBottom: 8,
      fontSize: 'clamp(0.9rem, 2.2vw, 1rem)', // Responsive font size
      lineHeight: 1.5
    },
    demoSection: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '60px 0', // Reduced padding for mobile
      minHeight: 'auto', // Remove fixed height
      width: '100%',
      boxSizing: 'border-box',
      id: 'demo-agents-section' // Added ID for scrolling
    },
    demoContainer: {
      display: 'flex',
      flexDirection: 'column', // Stack vertically on mobile
      gap: '24px', // Reduced gap for mobile
      alignItems: 'stretch',
      justifyContent: 'center',
      padding: '0 16px'
    },
    demoForm: {
      background: '#10131a',
      color: 'white',
      borderRadius: 24,
      padding: '20px',
      width: '100%', // Full width on mobile
      maxWidth: '400px',
      minWidth: 'auto', // Remove fixed min-width
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      boxSizing: 'border-box'
    },
    demoFormTitle: {
      fontWeight: 600,
      fontSize: 'clamp(1.2rem, 3vw, 1.6rem)', // Responsive font size
      marginBottom: 16,
      color: '#7dd3fc',
      lineHeight: 1.3
    },
    demoFormInput: {
      width: '100%',
      padding: '10px',
      borderRadius: 12,
      border: 'none',
      fontSize: '16px',
      marginBottom: '10px',
      background: 'white',
      color: '#222',
      boxSizing: 'border-box'
    },
    demoFormButton: {
      background: 'white',
      color: '#10131a',
      border: 'none',
      borderRadius: 12,
      padding: '10px 20px',
      fontWeight: 700,
      fontSize: '16px',
      cursor: 'pointer',
      marginTop: '2px',
      width: '140px'
    },
    demoImage: {
      width: '100%',
      maxWidth: '420px',
      borderRadius: 24,
      objectFit: 'cover',
      minHeight: '250px', // Reduced height for mobile
      alignSelf: 'center'
    },
    agentTypesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', // Responsive grid
      gap: '16px', // Reduced gap for mobile
      width: '100%',
      maxWidth: '400px',
      minWidth: 'auto', // Remove fixed min-width
      flex: '1 1 auto',
      alignContent: 'center',
      alignSelf: 'center'
    },
    agentTypeCard: {
      background: 'white',
      borderRadius: 16,
      padding: '20px 12px', // Reduced padding for mobile
      textAlign: 'center',
      fontWeight: 600,
      fontSize: 'clamp(14px, 3vw, 18px)', // Responsive font size
      color: '#10131a',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      minHeight: '100px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    agentTypeIcon: {
      fontSize: 'clamp(20px, 5vw, 28px)', // Responsive icon size
      display: 'block',
      marginBottom: '6px'
    },
    featuresGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '20px', // Reduced gap for mobile
      justifyContent: 'center',
      marginBottom: 40,
      padding: '0 16px'
    },
    featureCard: {
      flex: '1 1 220px',
      maxWidth: '320px',
      background: '#f3f4f6',
      borderRadius: 16,
      padding: '24px', // Reduced padding for mobile
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box'
    },
    featureIcon: {
      fontSize: 'clamp(30px, 6vw, 40px)', // Responsive icon size
      marginBottom: 16
    },
    featureTitle: {
      fontWeight: 700,
      fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', // Responsive font size
      marginBottom: 8,
      lineHeight: 1.3
    },
    featureDescription: {
      color: '#64748b',
      fontSize: 'clamp(0.9rem, 2.2vw, 1rem)', // Responsive font size
      lineHeight: 1.5
    },
    howItWorksGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '20px', // Reduced gap for mobile
      justifyContent: 'center',
      padding: '0 16px'
    },
    howItWorksCard: {
      flex: '1 1 220px',
      maxWidth: '320px',
      background: 'rgba(255,255,255,0.08)',
      borderRadius: 16,
      padding: '24px', // Reduced padding for mobile
      width: '100%',
      boxSizing: 'border-box'
    },
    howItWorksTitle: {
      fontWeight: 700,
      fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', // Responsive font size
      marginBottom: 8,
      lineHeight: 1.3
    },
    howItWorksDescription: {
      fontSize: 'clamp(0.9rem, 2.2vw, 1rem)', // Responsive font size
      lineHeight: 1.5
    },
    faqContainer: {
      display: 'flex',
      flexDirection: 'column', // Stack vertically on mobile
      gap: '24px', // Reduced gap for mobile
      alignItems: 'stretch'
    },
    faqAccordion: {
      flex: '1',
      width: '100%'
    },
    faqImage: {
      flex: '1',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '20px',
      width: '100%'
    },
    faqImageImg: {
      width: '100%',
      maxWidth: '250px',
      height: 'auto',
      borderRadius: 16,
      objectFit: 'cover',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }
  };

  // --- 6 SECTIONS ---
  return (
    <div className="chatbot-factory" style={{ overflowX: 'hidden', width: '100%' }}>
      <Header onLogin={() => setShowLogin(true)} onSignUp={() => setShowSignUp(true)} />

      {/* 1. Home (Hero) - Purple */}
      <section style={mobileStyles.heroSection}>
        <div style={mobileStyles.container}>
          <h1 style={mobileStyles.heroTitle}>Your Personal AI Receptionist — Always On, Always Ready</h1>
          <p style={mobileStyles.heroSubtitle}>Discover the new way to build, test, deploy, and monitor production-ready AI voice agents at scale.</p>
          <div style={mobileStyles.heroButtons}>
            <button onClick={handleContactSales} style={mobileStyles.heroButton}>Contact Sales</button>
            <button onClick={handleCreateChatbot} style={mobileStyles.heroButtonSecondary}>Create Your Chatbot</button>
          </div>
        </div>
      </section>

      {/* 2. Sectors - White */}
      <section style={mobileStyles.section}>
        <div style={mobileStyles.container}>
          <h2 style={mobileStyles.sectionTitle}>Sectors</h2>
          <div style={mobileStyles.sectorsGrid}>
            {/* Left Column: Cards */}
            <div style={mobileStyles.sectorsColumn}>
              {/* Charity Card (formerly Personal) */}
              <div style={mobileStyles.sectorCard}>
                <img src="https://cdn-icons-png.flaticon.com/512/1946/1946429.png" alt="Charity" style={mobileStyles.sectorImage} />
                <div style={mobileStyles.sectorContent}>
                  <h3 style={mobileStyles.sectorTitle}>{'Charity'}</h3>
                  <p style={mobileStyles.sectorDescription}>Support your cause with a voice AI assistant for donor enquiries, volunteer coordination, and charity communications.</p>
                </div>
              </div>
              {/* Business Card */}
              <div style={mobileStyles.sectorCard}>
                <img src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png" alt="Business" style={mobileStyles.sectorImage} />
                <div style={mobileStyles.sectorContent}>
                  <h3 style={mobileStyles.sectorTitle}>Business</h3>
                  <p style={mobileStyles.sectorDescription}>Streamline your business operations with a voice AI agent for customer support, appointments, and automation.</p>
                </div>
              </div>
            </div>
            
                                      {/* Right Column: Video */}
              <div style={mobileStyles.videoColumn}>
                <video 
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    borderRadius: '16px',
                    display: 'block'
                  }}
                  controls
                  autoPlay
                  muted
                  loop
                  poster="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=600&q=80"
                >
                  {/* Show different videos based on screen size */}
                  <source 
                    src={window.innerWidth <= 768 ? "/CBF_Promo_Video.mp4" : "/CBF_Promo_Video.mp4"} 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
          </div>
        </div>
      </section>

      {/* 3. Demo Agents - Purple */}
      <section id="demo-agents-section" style={mobileStyles.demoSection}>
        <div style={mobileStyles.container}>
          <h2 style={mobileStyles.sectionTitle}>Demo Trial</h2>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', // Horizontal layout on desktop
            flexWrap: 'wrap', // Wrap on mobile
            gap: '32px', 
            alignItems: 'flex-start', // Align items to top
            justifyContent: 'center',
            padding: '0 16px'
          }}>
            {/* Left: Form with Agent List */}
            <div style={{
              ...mobileStyles.demoForm,
              width: '100%',
              maxWidth: '600px', // Increased width
              minWidth: '500px', // Increased minimum width
              flex: '1 1 500px'
            }}>
              
              {formStep === 1 ? (
                <>
                  {/* Step 1: Agent Selection */}
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Request a demo from our selected agent</h3>
                    <label style={{ display: 'block', marginBottom: 12, fontWeight: 500, fontSize: '0.9rem', color: 'white' }}>Available Agents</label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      {agents.map((agent, index) => (
                        <button 
                          key={agent.id}
                          onClick={() => handleAgentSelection(agent)}
                          style={{
                            background: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            outline: 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                          }}>
                          <div style={{ fontSize: '24px' }}>{agent.icon}</div>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{agent.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Step 2: User Details Form */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <button 
                        onClick={handleBackToAgentSelection}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '16px',
                          marginRight: '12px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}>
                        ← Back
                      </button>
                    </div>
                    
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.1)', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      marginBottom: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                                             <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>
                         Selected Agent: <span style={{ color: '#7dd3fc' }}>{selectedAgent.name}</span>
                       </span>
                    </div>
                  </div>
                  
                  <form onSubmit={handleFormSubmit}>
                    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ fontWeight: 500, fontSize: '0.9rem', color: 'white', minWidth: '100px' }}>Name:</label>
                      <input type="text" name="name" placeholder="John" style={{...mobileStyles.demoFormInput, flex: 1}} required />
                    </div>
                    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ fontWeight: 500, fontSize: '0.9rem', color: 'white', minWidth: '100px' }}>Phone Number:</label>
                      <input type="tel" name="phone" placeholder="+61 123 456 789" style={{...mobileStyles.demoFormInput, flex: 1}} required />
                    </div>
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ fontWeight: 500, fontSize: '0.9rem', color: 'white', minWidth: '100px' }}>Email:</label>
                      <input type="email" name="email" placeholder="john@company.com" style={{...mobileStyles.demoFormInput, flex: 1}} required />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                      <button type="submit" style={mobileStyles.demoFormButton}>SUBMIT DEMO REQUEST</button>
                    </div>
                  </form>
                </>
              )}
            </div>
            {/* Center: Image */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flex: '1 1 500px',
              minWidth: '500px',
              height: '400px' // Match the form box height
            }}>
              <img src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=600&q=80" alt="Receptionist" style={{
                ...mobileStyles.demoImage,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 24
              }} />
            </div>
          </div>
        </div>
      </section>

      {/* 4. Features & Form - White */}
      <section style={mobileStyles.section}>
        <div style={mobileStyles.container}>
          <h2 style={mobileStyles.sectionTitle}>Features & Get in Touch</h2>
          <div style={mobileStyles.featuresGrid}>
            <div style={mobileStyles.featureCard}>
              <div style={mobileStyles.featureIcon}>🤖</div>
              <h3 style={mobileStyles.featureTitle}>Auto-Sync Knowledge Base</h3>
              <p style={mobileStyles.featureDescription}>Your AI agents stay up-to-date effortlessly by syncing directly with your company's knowledge base.</p>
            </div>
            <div style={mobileStyles.featureCard}>
              <div style={mobileStyles.featureIcon}>📞</div>
              <h3 style={mobileStyles.featureTitle}>Effortless Deployment</h3>
              <p style={mobileStyles.featureDescription}>Deploy your agents to phone calls, web calls, SMS, and more with a single click.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Guide Steps - Purple */}
      <section style={{ ...mobileStyles.demoSection, textAlign: 'center' }}>
        <div style={mobileStyles.container}>
          <h2 style={mobileStyles.sectionTitle}>How It Works</h2>
                     <div style={mobileStyles.howItWorksGrid}>
             <div style={mobileStyles.howItWorksCard}>
               <h3 style={mobileStyles.howItWorksTitle}>Create Agent</h3>
               <p style={mobileStyles.howItWorksDescription}>Our system creates your custom AI agent based on your specifications.</p>
             </div>
             <div style={mobileStyles.howItWorksCard}>
               <h3 style={mobileStyles.howItWorksTitle}>Test Agent</h3>
               <p style={mobileStyles.howItWorksDescription}>Test your agent thoroughly to ensure it meets your requirements.</p>
             </div>
                           <div style={mobileStyles.howItWorksCard}>
                <h3 style={mobileStyles.howItWorksTitle}>Start Using</h3>
                <p style={mobileStyles.howItWorksDescription}>Start making and receiving calls with your AI agent immediately.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 6. FAQs - White */}
      <section style={mobileStyles.section}>
        <div style={mobileStyles.container}>
          <h2 style={mobileStyles.sectionTitle}>Frequently Asked Questions</h2>
          <div style={{
            display: 'flex',
            flexDirection: 'row', // Horizontal layout
            flexWrap: 'wrap', // Wrap on mobile
            gap: '40px',
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}>
            {/* Left: Accordion */}
            <div style={{
              flex: '0 0 60%',
              minWidth: '300px'
            }}>
              <div style={{ marginBottom: 8 }}>
                <div 
                  onClick={() => handleFAQToggle(0)}
                  style={{ 
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h4 style={{ fontWeight: 700, margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: 1.3 }}>How do I create an AI Voice Agent?</h4>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                    {openFAQ === 0 ? '−' : '+'}
                  </span>
                </div>
                                 {openFAQ === 0 && (
                   <div style={{ 
                     padding: '12px 16px', 
                     background: 'white', 
                     border: '1px solid #e2e8f0',
                     borderTop: 'none',
                     borderRadius: '0 0 12px 12px',
                     color: '#64748b',
                     lineHeight: 1.6,
                     fontSize: 'clamp(0.9rem, 2.2vw, 1rem)'
                   }}>
                     We create your AI Voice Agent for you and personalise the content to your needs. Based on your specifications, we set up your agent's name, voice, and greeting. Your Personal Answering Assistant will be ready to screen calls, take messages, and send you real-time text summaries so you can decide whether to answer, call back later, or ignore the call.
                   </div>
                 )}
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <div 
                  onClick={() => handleFAQToggle(1)}
                  style={{ 
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h4 style={{ fontWeight: 700, margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: 1.3 }}>What is the pricing?</h4>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                    {openFAQ === 1 ? '−' : '+'}
                  </span>
                </div>
                                 {openFAQ === 1 && (
                   <div style={{ 
                     padding: '12px 16px', 
                     background: 'white', 
                     border: '1px solid #e2e8f0',
                     borderTop: 'none',
                     borderRadius: '0 0 12px 12px',
                     color: '#64748b',
                     lineHeight: 1.6,
                     fontSize: 'clamp(0.9rem, 2.2vw, 1rem)'
                   }}>
                     Our Personal Answering Assistant is now offered as a fixed amount for a set time period (e.g. a fixed monthly or annual fee that includes a defined usage limit). This includes custom assistant name and voice, call screening and summarisation, and real-time text updates. Optional premium add-ons such as advanced spam blocking, Do Not Disturb scheduling, call-back scheduling, and analytics are also available — add-on pricing varies and is quoted on request. For current fixed plans and time limits, please contact us or check the latest pricing on the platform.
                   </div>
                 )}
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <div 
                  onClick={() => handleFAQToggle(2)}
                  style={{ 
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h4 style={{ fontWeight: 700, margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: 1.3 }}>Can I connect to my existing phone number?</h4>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                    {openFAQ === 2 ? '−' : '+'}
                  </span>
                </div>
                                 {openFAQ === 2 && (
                   <div style={{ 
                     padding: '12px 16px', 
                     background: 'white', 
                     border: '1px solid #e2e8f0',
                     borderTop: 'none',
                     borderRadius: '0 0 12px 12px',
                     color: '#64748b',
                     lineHeight: 1.6,
                     fontSize: 'clamp(0.9rem, 2.2vw, 1rem)'
                   }}>
                     You can connect the AI Voice Agent to your current phone number so incoming calls are screened by your assistant before they reach you. Our setup process will walk you through linking your number and configuring call handling preferences.
                   </div>
                 )}
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <div 
                  onClick={() => handleFAQToggle(3)}
                  style={{ 
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h4 style={{ fontWeight: 700, margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: 1.3 }}>How secure is the platform?</h4>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                    {openFAQ === 3 ? '−' : '+'}
                  </span>
                </div>
                                 {openFAQ === 3 && (
                   <div style={{ 
                     padding: '12px 16px', 
                     background: 'white', 
                     border: '1px solid #e2e8f0',
                     borderTop: 'none',
                     borderRadius: '0 0 12px 12px',
                     color: '#64748b',
                     lineHeight: 1.6,
                     fontSize: 'clamp(0.9rem, 2.2vw, 1rem)'
                   }}>
                     Chatbot Factory is committed to keeping your data secure. All customer conversations are encrypted, and we never sell your information. You have full control over your data through your personalised dashboard, and can cancel or modify your subscription at any time. We also offer secure payment processing and account management.
                   </div>
                 )}
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <div 
                  onClick={() => handleFAQToggle(4)}
                  style={{ 
                    padding: '12px 16px', 
                    background: '#f8fafc', 
                    borderRadius: 12, 
                    cursor: 'pointer',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <h4 style={{ fontWeight: 700, margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.1rem)', lineHeight: 1.3 }}>What integrations are available?</h4>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                    {openFAQ === 4 ? '−' : '+'}
                  </span>
                </div>
                                 {openFAQ === 4 && (
                   <div style={{ 
                     padding: '12px 16px', 
                     background: 'white', 
                     border: '1px solid #e2e8f0',
                     borderTop: 'none',
                     borderRadius: '0 0 12px 12px',
                     color: '#64748b',
                     lineHeight: 1.6,
                     fontSize: 'clamp(0.9rem, 2.2vw, 1rem)'
                   }}>
                     Your personalised Chatbot Factory dashboard integrates with our call-screening and notification system out of the box. Premium add-ons offer extended functionality, including spam filtering, multilingual support, and an enhanced analytics dashboard. Additional integrations can be requested via our support team.
                   </div>
                 )}
              </div>
            </div>
            
            {/* Right: Image */}
            <div style={{
              flex: '0 0 30%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: '20px',
              minWidth: '250px'
            }}>
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=400&q=80" 
                alt="Customer Support" 
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  borderRadius: 16,
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer and modals remain unchanged */}
      <Footer />
      {/* Ensure the Login modal is always present and controlled by showLogin */}
      <Login isOpen={showLogin} onClose={() => setShowLogin(false)} onSwitchToSignUp={handleSwitchToSignUp} />
      <SignUp
        isOpen={showSignUp}
        onClose={() => setShowSignUp(false)}
        onSwitchToLogin={handleSwitchToLogin}
        initialBasicDetails={signUpPrefill}
      />
      
      {/* Contact Sales Modal */}
      {showContactSales && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={handleContactSalesClose}
              style={{
                position: 'absolute',
                top: '15px',
                right: '20px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                fontWeight: 'bold'
              }}
            >
              ×
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h2 style={{
                fontSize: '2em',
                color: '#4f46e5',
                marginBottom: '10px',
                fontWeight: '700'
              }}>
                🎯 Contact Sales Demo
              </h2>
              <p style={{
                fontSize: '1.1em',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                Experience our AI sales agent in action. Fill in your details below to start the demo.
              </p>
            </div>

                         {/* Form */}
             {!isFormHidden && (
               <form onSubmit={(e) => { e.preventDefault(); handleStartDemo(); }}>
                 <div style={{ marginBottom: '20px' }}>
                   <label style={{
                     display: 'block',
                     marginBottom: '8px',
                     fontWeight: '600',
                     color: '#374151'
                   }}>
                     Full Name <span style={{ color: '#ef4444' }}>*</span>
                   </label>
                   <input
                     type="text"
                     name="name"
                     value={contactSalesData.name}
                     onChange={handleContactSalesInputChange}
                     required
                     placeholder="Enter your full name"
                     style={{
                       width: '100%',
                       padding: '12px 16px',
                       border: '2px solid #e5e7eb',
                       borderRadius: '10px',
                       fontSize: '16px',
                       transition: 'border-color 0.3s ease'
                     }}
                   />
                 </div>

                 <div style={{ marginBottom: '20px' }}>
                   <label style={{
                     display: 'block',
                     marginBottom: '8px',
                     fontWeight: '600',
                     color: '#374151'
                   }}>
                     Email Address <span style={{ color: '#ef4444' }}>*</span>
                   </label>
                   <input
                     type="email"
                     name="email"
                     value={contactSalesData.email}
                     onChange={handleContactSalesInputChange}
                     required
                     placeholder="Enter your email address"
                     style={{
                       width: '100%',
                       padding: '12px 16px',
                       border: '2px solid #e5e7eb',
                       borderRadius: '10px',
                       fontSize: '16px',
                       transition: 'border-color 0.3s ease'
                     }}
                   />
                 </div>

                 <div style={{ marginBottom: '30px' }}>
                   <label style={{
                     display: 'block',
                     marginBottom: '8px',
                     fontWeight: '600',
                     color: '#374151'
                   }}>
                     Phone Number <span style={{ color: '#ef4444' }}>*</span>
                   </label>
                   <input
                     type="tel"
                     name="phone"
                     value={contactSalesData.phone}
                     onChange={handleContactSalesInputChange}
                     required
                     placeholder="Enter your phone number"
                     style={{
                       width: '100%',
                       padding: '12px 16px',
                       border: '2px solid #e5e7eb',
                       borderRadius: '10px',
                       fontSize: '16px',
                       transition: 'border-color 0.3s ease'
                     }}
                   />
                 </div>

                                              {/* Call Status Display */}
                 {callStatus && (
                   <div style={{
                     marginBottom: '20px',
                     padding: '12px',
                     borderRadius: '8px',
                     background: callError ? '#fef2f2' : '#f0f9ff',
                     border: callError ? '1px solid #fecaca' : '1px solid #bae6fd',
                     color: callError ? '#dc2626' : '#0369a1',
                     fontSize: '14px',
                     textAlign: 'center'
                   }}>
                     {callError ? `❌ ${callError}` : `📞 ${callStatus}`}
                   </div>
                 )}

                 {/* Start Demo Button or End Call Button */}
                 {!isCallActive ? (
                   <button
                     type="submit"
                     disabled={isStartingDemo || isConnecting}
                     style={{
                       background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                       color: 'white',
                       padding: '16px 40px',
                       border: 'none',
                       borderRadius: '10px',
                       fontSize: '18px',
                       fontWeight: '600',
                       cursor: (isStartingDemo || isConnecting) ? 'not-allowed' : 'pointer',
                       transition: 'transform 0.2s ease',
                       width: '100%',
                       opacity: (isStartingDemo || isConnecting) ? 0.7 : 1
                     }}
                   >
                     {isStartingDemo || isConnecting ? 'Starting Demo...' : '🚀 Start Demo'}
                   </button>
                 ) : (
                   <button
                     type="button"
                     onClick={endCall}
                     style={{
                       background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                       color: 'white',
                       padding: '16px 40px',
                       border: 'none',
                       borderRadius: '10px',
                       fontSize: '18px',
                       fontWeight: '600',
                       cursor: 'pointer',
                       transition: 'transform 0.2s ease',
                       width: '100%'
                     }}
                   >
                     📞 End Call
                   </button>
                 )}
               </form>
             )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage; 